import React, { useEffect, useRef, useMemo, ComponentType, useCallback, memo } from 'react';
import dynamic from 'next/dynamic';
import { buildClassName } from 'utils/build-class-name';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import styles from './style.module.scss';
import { testIds } from 'constants/test-constants';
import { QuebecPopupOptions, getQuebecTrackingEvents } from 'components/modal-dialog/quebec-popup';
import { trackEvent } from 'utils/google-tag-manager';
import * as ModalNames from 'contexts/modal';
import { useThemeContext, useUserContext } from 'contexts';
import { ThemeNames } from 'types/themes';
import { GTM_HOMEPAGE_SIGN_UP_FROM_URL_CLOSE } from 'constants/events';
import {
  ExpCloseButton,
  DefaultCloseButton,
} from './close-buttons';

import type { ModalName, ModalProps } from 'contexts/modal';

const MODAL_COMPONENTS: Record<ModalName, ComponentType<ModalProps<any>>> = {
  [ModalNames.LISTING_MODAL]: dynamic(import('components/modal-dialog/listing')),
  [ModalNames.CONFIRM_ACCOUNT_MODAL]: dynamic(import('components/modal-dialog/user/confirm-account')),
  [ModalNames.CONNECTION_REQUEST_MOBILE_MODAL]: dynamic(import('components/modal-dialog/connection-request')),
  [ModalNames.CONNECTION_REQUEST_POPUP_MODAL]: dynamic(import('components/modal-dialog/connection-request-popup')),
  [ModalNames.CONNECT_WITH_AGENT_MODAL]: dynamic(import('components/modal-dialog/connect-with-an-agent')),
  [ModalNames.TERMS_OF_USE_MODAL]: dynamic(import('components/modal-dialog/terms-of-use')),
  [ModalNames.NOTIFICATION_MODAL]: dynamic(import('components/modal-dialog/notification')),
  [ModalNames.CREATE_SAVED_SEARCH_MODAL]: dynamic(import('components/modal-dialog/create-saved-search')),
  [ModalNames.ACTIVATE_ACCOUNT_MODAL]: dynamic(import('components/modal-dialog/user/activate-account')),
  [ModalNames.FORGOT_PASSWORD_MODAL]: dynamic(import('components/modal-dialog/user/forgot-password')),
  [ModalNames.RESET_PASSWORD_MODAL]: dynamic(import('components/modal-dialog/user/reset-password')),
  [ModalNames.CHANGE_PASSWORD_MODAL]: dynamic(import('components/modal-dialog/user/profile-page/change-password')),
  [ModalNames.CREATE_PASSWORD_MODAL]: dynamic(import('components/modal-dialog/user/profile-page/create-password')),
  [ModalNames.CHANGE_EMAIL_MODAL]: dynamic(import('components/modal-dialog/user/profile-page/change-email')),
  [ModalNames.LOGIN_REGISTRATION_MODAL]: dynamic(import('components/modal-dialog/user/login-registration')),
  [ModalNames.PHONE_NUMBER_MODAL]: dynamic(import('components/modal-dialog/user/phone-number')),
  [ModalNames.PHONE_NUMBER_AND_EMAIL_MODAL]: dynamic(import('components/modal-dialog/user/phone-number-and-email')),
  [ModalNames.NEWSLETTER_MODAL]: dynamic(import('components/modal-dialog/user/newsletter')),
  [ModalNames.PRIVACY_TERMS_MODAL]: dynamic(import('components/modal-dialog/user/privacy-terms')),
  [ModalNames.LIGHTBOX_MODAL]: dynamic(import('components/modal-dialog/lightbox')),
  [ModalNames.DELETE_PROMPT_MODAL]: dynamic(import('components/modal-dialog/delete-prompt')),
  [ModalNames.SAVED_SEARCH_FILTERS_MODAL]: dynamic(import('components/modal-dialog/saved-search-filters')),
  [ModalNames.MY_LINK_MY_LEAD_CREATE_MODAL]: dynamic(import('components/modal-dialog/my-link-my-lead-create')),
  [ModalNames.SCHOOL_FILTERS_MODAL]: dynamic(import('components/search/map/school-filters')),
  [ModalNames.WELCOME_MODAL]: dynamic(import('components/modal-dialog/user/welcome')),
  [ModalNames.PHOTO_GALLERY_MODAL]: dynamic(import('components/modal-dialog/photo-gallery')),
  [ModalNames.PRECON_PHOTO_GALLERY_MODAL]: dynamic(import('components/modal-dialog/precon-photo-gallery')),
  [ModalNames.PRECON_SUCCESS_NOTIFICATION_MODAL]: dynamic(import('components/modal-dialog/precon-success-notification')),
  [ModalNames.PRECON_REQUEST_MODAL]: dynamic(import('components/modal-dialog/precon-request')),
  [ModalNames.QUEBEC_POPUP_MODAL]: dynamic(import('components/modal-dialog/quebec-popup')),
  [ModalNames.HOME_APPRAISAL_CONNECTION_REQUEST_POPUP_MODAL]: dynamic(import('components/modal-dialog/home-appraisal-connection-request-popup')),
  [ModalNames.HOME_APPRAISAL_CONNECTION_REQUEST_MODAL]: dynamic(import('components/modal-dialog/home-appraisal-connection-request')),
} as const;

export default function Modal({
  name,
  options,
  openModal,
  closeModal,
  closeAllModals,
  changeUrlOnModal,
  isActiveModal,
  isCrawler,
}: ModalProps) {

  const { themeName } = useThemeContext();
  const { isOnSignUpUrl, setIsOnSignUpUrl } = useUserContext();
  const nameRef = useRef(name);

  const isExpTheme = themeName !== ThemeNames.ZOOCASA;
  const isConnectionRequestDrawer = name === ModalNames.CONNECTION_REQUEST_MOBILE_MODAL || name === ModalNames.HOME_APPRAISAL_CONNECTION_REQUEST_MODAL;
  const isImportant = name === ModalNames.TERMS_OF_USE_MODAL;
  const modalsClosePrevention = [ModalNames.PHONE_NUMBER_MODAL, ModalNames.PHONE_NUMBER_AND_EMAIL_MODAL];
  const modalContainerClassName = buildClassName(styles.component, isConnectionRequestDrawer && styles.drawer, isImportant && styles.important);
  const modalClassName = buildClassName(styles.modal, styles[name] ? styles[name] : '', isExpTheme && styles['exp-theme']);

  const modalContentElement = useOutsideClickHandler<HTMLDivElement>(() => {
    if (!modalsClosePrevention.includes(nameRef.current)) {
      if (name === ModalNames.TERMS_OF_USE_MODAL) {
        closeAllModals();
      } else {
        if (name === ModalNames.LOGIN_REGISTRATION_MODAL && isOnSignUpUrl) {
          setIsOnSignUpUrl(false);
          trackEvent(GTM_HOMEPAGE_SIGN_UP_FROM_URL_CLOSE);
        }
        closeModal();
      }
    }
  });

  const handleClose = useCallback(() => {
    if (name === ModalNames.TERMS_OF_USE_MODAL) {
      closeAllModals();
    } else {
      closeModal();
    }

    if (name === ModalNames.QUEBEC_POPUP_MODAL) {
      trackEvent(getQuebecTrackingEvents((options as QuebecPopupOptions).source).close);
    }

    if (name === ModalNames.LOGIN_REGISTRATION_MODAL && isOnSignUpUrl) {
      setIsOnSignUpUrl(false);
      trackEvent(GTM_HOMEPAGE_SIGN_UP_FROM_URL_CLOSE);
    }
  }, [name, isOnSignUpUrl, closeAllModals, closeModal, options, setIsOnSignUpUrl]);

  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  const CloseButton = isExpTheme ? ExpCloseButton : DefaultCloseButton;
  const ModalContentMemo = useMemo(() => MODAL_COMPONENTS[name], [name]);

  return (
    <div className={modalContainerClassName} data-testid={name}>
      <div ref={isActiveModal ? modalContentElement : null} className={modalClassName} data-testid={testIds.modalInner}>
        {!modalsClosePrevention.includes(name) && <CloseButton onClose={handleClose} testId={name} />}
        <div className={styles.content}>
          {ModalContentMemo &&
            <ModalContentMemo
              name={name}
              openModal={openModal}
              closeModal={closeModal}
              closeAllModals={closeAllModals}
              changeUrlOnModal={changeUrlOnModal}
              options={options}
              isCrawler={isCrawler}
            />
          }
        </div>
      </div>
    </div>
  );
}
